import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, Card, LoadingState, EmptyState, ErrorState, Pill } from "@/components/common/UI";
import { useAuth } from "@/lib/auth";
import { fmtINR, fmtDate, fmtPct } from "@/lib/format";
import { CheckCircle2 } from "lucide-react";

export default function PendingApproval() {
    const { user } = useAuth();
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/rfqs/pending-my-approval").then((r) => setRows(r.data)).catch((e) => setError(formatApiError(e)));
    }, []);

    const isDirector = user.role === "DIRECTOR";
    const urgent = (r) => r.response_due_date && new Date(r.response_due_date).getTime() - Date.now() < 24 * 60 * 60 * 1000;
    const overdue = (r) => r.response_due_date && new Date(r.response_due_date) < new Date();

    return (
        <div data-testid="rfq-pending-approval-page">
            <PageHeader
                eyebrow="MY QUEUE"
                title={isDirector ? "Pending Director Action" : "RFQs Pending My Approval"}
                subtitle={isDirector ? "Escalated RFQs (below 8% margin threshold) awaiting your decision." : "RFQs awaiting your decision. Sorted by due date — most urgent first."}
            />
            {error && <ErrorState message={error} />}
            {!error && rows === null && <LoadingState />}
            {rows && rows.length === 0 && <EmptyState icon={CheckCircle2} title="Inbox zero" description="No RFQs awaiting your decision." />}
            {rows && rows.length > 0 && (
                <Card>
                    <div className="overflow-x-auto">
                        <table className="w-full table-rowhover">
                            <thead><tr className="border-b border-slate-200 bg-slate-50/60">
                                <th className="eyebrow text-left py-2 px-3 whitespace-nowrap">Reference</th>
                                <th className="eyebrow text-left py-2 px-3 whitespace-nowrap">Customer</th>
                                <th className="eyebrow text-right py-2 px-3 whitespace-nowrap">Parts</th>
                                <th className="eyebrow text-right py-2 px-3 whitespace-nowrap">Final Price</th>
                                <th className="eyebrow text-right py-2 px-3 whitespace-nowrap">Margin</th>
                                <th className="eyebrow text-left py-2 px-3 whitespace-nowrap">Submitted</th>
                                <th className="eyebrow text-left py-2 px-3 whitespace-nowrap">Urgency</th>
                            </tr></thead>
                            <tbody>
                                {rows.map((r) => {
                                    const p = r.pricing || {};
                                    return (
                                        <tr key={r.id} className="border-b border-slate-100">
                                            <td className="py-1.5 px-3 font-mono font-semibold text-[13px] whitespace-nowrap"><Link to={`/rfq/${r.id}`} className="hover:text-[hsl(var(--copper))]">{r.reference}</Link></td>
                                            <td className="py-1.5 px-3 text-[13px] whitespace-nowrap">{r.customer_name}</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px]">{(r.parts || []).length}</td>
                                            <td className="py-1.5 px-3 text-right font-mono tabular-nums text-[13px] font-semibold whitespace-nowrap">{fmtINR(p.final_price || 0)}</td>
                                            <td className={`py-1.5 px-3 text-right font-mono tabular-nums text-[13px] ${p.margin_pct >= 15 ? "text-emerald-600" : p.margin_pct >= 8 ? "text-amber-600" : "text-red-600"}`}>{fmtPct(p.margin_pct || 0)}</td>
                                            <td className="py-1.5 px-3 text-[11px] font-mono text-slate-500 whitespace-nowrap">{fmtDate(r.submitted_at || r.created_at)}<br/><span className="text-slate-400">{r.submitted_by_name || r.created_by_name}</span></td>
                                            <td className="py-1.5 px-3 whitespace-nowrap">
                                                {overdue(r) ? <Pill tone="solid_red">Overdue</Pill> : urgent(r) ? <Pill tone="red">&lt; 24h</Pill> : <Pill tone="slate">On time</Pill>}
                                                <div className="text-[10px] text-slate-500 mt-1">Due {fmtDate(r.response_due_date)}</div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}
        </div>
    );
}
